import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Pagination, Stack, Typography,
} from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import { PayrollService } from '../../services/payroll.service';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { IMyPayslipDetail, IMyPayslipSummary } from '../../interfaces/payroll.interface';
import { formatMoney, humanizeEnum, payslipFileBase } from '../../utils/format';

const PAGE_SIZE = 12;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const periodLabel = (year: number, month: number): string => `${MONTHS[month - 1] ?? month} ${year}`;

const LineTable: React.FC<{ title: string; lines: { name: string; amount: number }[]; total: number }> = ({ title, lines, total }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{title}</Typography>
        {lines.length === 0 && <Typography variant="body2" color="text.secondary">None</Typography>}
        {lines.map((l, i) => (
            <Stack key={`${l.name}-${i}`} direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                <Typography variant="body2" sx={{ pr: 2, wordBreak: 'break-word' }}>{l.name}</Typography>
                <Typography variant="body2">{formatMoney(l.amount, 'BDT')}</Typography>
            </Stack>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontWeight={600}>Total</Typography>
            <Typography variant="body2" fontWeight={600}>{formatMoney(total, 'BDT')}</Typography>
        </Stack>
    </Box>
);

const MyPayslips: React.FC = () => {
    const { user, selectedInstitutionId } = useAuth();
    const toast = useToast();
    const institutionId = selectedInstitutionId || AuthService.getSelectedInstitution() || '';
    const [rows, setRows] = useState<IMyPayslipSummary[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState<IMyPayslipDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [downloading, setDownloading] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!institutionId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await PayrollService.listMyPayslips(institutionId, page, PAGE_SIZE);
            setRows(res.content);
            setTotalPages(res.totalPages);
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load your payslips');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, page]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (id: string) => {
        setDetailLoading(true);
        setDetail(null);
        try {
            setDetail(await PayrollService.getMyPayslip(id));
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load the payslip');
        } finally {
            setDetailLoading(false);
        }
    };

    const download = async (p: { id: string; year: number; month: number }, lang: 'EN' | 'BN') => {
        setDownloading(`${p.id}-${lang}`);
        try {
            await PayrollService.downloadMyPayslip(p.id, lang, payslipFileBase({ userName: user?.username, year: p.year, month: p.month }));
        } catch (e: any) {
            toast.showApiError(e, 'Failed to download the payslip');
        } finally {
            setDownloading(null);
        }
    };

    const pdfButtons = (p: { id: string; year: number; month: number }) => (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button data-help="payslips.download" size="small" variant="outlined" disabled={downloading !== null} onClick={() => download(p, 'EN')}>
                Download PDF
            </Button>
            <Button data-help="payslips.download" size="small" variant="outlined" disabled={downloading !== null} onClick={() => download(p, 'BN')}>
                বাংলা PDF
            </Button>
        </Stack>
    );

    const columns: Column<IMyPayslipSummary>[] = [
        { key: 'period', header: 'Month', helpKey: 'payslips.mine.col.period', priority: 1, render: r => periodLabel(r.year, r.month) },
        { key: 'net', header: 'Net pay', helpKey: 'payslips.mine.col.net', priority: 1, align: 'right', render: r => formatMoney(r.netSalary) },
        { key: 'gross', header: 'Gross', helpKey: 'payslips.mine.col.gross', priority: 3, align: 'right', render: r => formatMoney(r.grossSalary) },
        { key: 'status', header: 'Status', helpKey: 'payslips.mine.status', priority: 2, render: r => <Chip data-help="payslips.mine.status" size="small" color="success" label={humanizeEnum(r.status)} /> },
        {
            key: 'actions', header: 'Actions', helpKey: 'payslips.mine.col.actions', role: 'actions', hideInCard: true,
            render: r => (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Button data-help="payslips.mine.action.view" size="small" onClick={() => openDetail(r.id)}>View</Button>
                    {pdfButtons(r)}
                </Stack>
            ),
        },
    ];

    return (
        <Box data-help-section="payslips.mine" data-help-page="payslips.mine.page" sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="h5" sx={{ mb: 2 }}>My Payslips</Typography>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <>
                    <ResponsiveTable data-help-section="payslips.mine.table"
                        pageKey="my-payslips"
                        columns={columns}
                        rows={rows}
                        getRowKey={r => r.id}
                        emptyMessage="No payslips yet. Payslips appear here once your payroll for a month is finalized."
                    />
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination count={totalPages} page={page + 1} onChange={(_, p) => setPage(p - 1)} />
                        </Box>
                    )}
                </>
            )}

            <Dialog open={detailLoading || !!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{detail ? `Payslip — ${periodLabel(detail.year, detail.month)}` : 'Payslip'}</DialogTitle>
                <DialogContent dividers>
                    {detailLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
                    {detail && (
                        <>
                            {detail.payableDays != null && detail.periodDays != null && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Payable days {detail.payableDays} / {detail.periodDays}
                                </Typography>
                            )}
                            {detail.daysInMonth != null && (
                                <Box data-help="payslips.mine.attendance" sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Attendance</Typography>
                                    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                                        <Typography variant="body2">Days in month</Typography>
                                        <Typography variant="body2">{detail.daysInMonth}</Typography>
                                    </Stack>
                                    {detail.absentDays != null && (
                                        <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                                            <Typography variant="body2">Days absent</Typography>
                                            <Typography variant="body2">{detail.absentDays}</Typography>
                                        </Stack>
                                    )}
                                    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                                        <Typography variant="body2">Unpaid leave days</Typography>
                                        <Typography variant="body2">{detail.unpaidLeaveDays ?? 0}</Typography>
                                    </Stack>
                                    {(detail.overtimeAmount ?? 0) > 0 && (
                                        <Box data-help="payslips.mine.overtime">
                                            <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                                                <Typography variant="body2">Overtime hours</Typography>
                                                <Typography variant="body2">{detail.overtimeHours}</Typography>
                                            </Stack>
                                            {detail.overtimeRate != null && (
                                                <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                                                    <Typography variant="body2">Overtime rate per hour</Typography>
                                                    <Typography variant="body2">{formatMoney(detail.overtimeRate, 'BDT')}</Typography>
                                                </Stack>
                                            )}
                                            <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                                                <Typography variant="body2">Overtime amount</Typography>
                                                <Typography variant="body2">{formatMoney(detail.overtimeAmount, 'BDT')}</Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            <LineTable title="Earnings" lines={detail.earnings} total={detail.totalEarnings} />
                            <LineTable title="Deductions" lines={detail.deductions} total={detail.totalDeductions} />
                            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                                <Typography variant="body2">Gross salary</Typography>
                                <Typography variant="body2">{formatMoney(detail.grossSalary, 'BDT')}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="subtitle1" fontWeight={700}>Net pay</Typography>
                                <Typography variant="subtitle1" fontWeight={700}>{formatMoney(detail.netSalary, 'BDT')}</Typography>
                            </Stack>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', px: 2 }}>
                    {detail ? pdfButtons(detail) : <span />}
                    <Button onClick={() => setDetail(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MyPayslips;
